import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, RotateCcw, Server, Zap, Loader2, ListChecks } from 'lucide-react';
import { Pagination } from '../../components/client';
import { useAdminFailedOperations } from '../../hooks/useAdminFailedOperations';
import { useToastContext } from '../../contexts/ToastContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const RetryButton = ({ onClick, disabled, label = 'Retry' }) => (
 <button
 type="button"
 onClick={onClick}
 disabled={disabled}
 className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-border rounded-md bg-surface text-ink hover:bg-surface-muted disabled:opacity-50"
 >
 <RotateCcw className="w-3.5 h-3.5" />
 {label}
 </button>
);

const EmptyRow = ({ message }) => (
 <tr>
 <td colSpan={5} className="px-4 py-8 text-center text-sm text-ink-tertiary">
 {message}
 </td>
 </tr>
);

const AdminFailedOperations = () => {
 const [activeTab, setActiveTab] = useState('queue');
 const [pages, setPages] = useState({ queue: 1, outbox: 1, stripe: 1 });
 const toast = useToastContext();
 const {
 summary,
 perPage,
 queueJobs,
 queueMeta,
 outboxEvents,
 outboxMeta,
 stripeWebhooks,
 stripeMeta,
 isLoading,
 refetch,
 retryQueueJob,
 retryOutboxEvent,
 retryStripeWebhook,
 retryAllQueueJobs,
 retryAllOutboxEvents,
 retryAllStripeWebhooks,
 isRetrying,
 } = useAdminFailedOperations({
 queuePage: pages.queue,
 outboxPage: pages.outbox,
 stripePage: pages.stripe,
 });

 const activeMeta =
 activeTab === 'queue' ? queueMeta : activeTab === 'outbox' ? outboxMeta : stripeMeta;

 const handlePageChange = (page) => {
 setPages((current) => ({ ...current, [activeTab]: page }));
 };

 const tabs = [
 { id: 'queue', label: 'Queue jobs', count: summary?.queue_failed ?? 0, bulkLimit: 50 },
 { id: 'outbox', label: 'Outbox events', count: summary?.outbox_failed ?? 0, bulkLimit: 50 },
 { id: 'stripe', label: 'Stripe webhooks', count: summary?.stripe_failed ?? 0, bulkLimit: 25 },
 ];

 const activeTabConfig = tabs.find((t) => t.id === activeTab);

 const handleBulkRetry = () => {
 const tab = activeTabConfig;
 if (!tab || tab.count === 0) {
 return;
 }

 const limit = Math.min(tab.bulkLimit, tab.count);
 const confirmed = window.confirm(
 `Retry up to ${limit} failed ${tab.label.toLowerCase()}?`
 );
 if (!confirmed) {
 return;
 }

 const onSuccess = (response) => {
 const stats = response?.data || {};
 const message = response?.message || 'Bulk retry completed';
 if ((stats.failed ?? 0) > 0) {
 toast.error(null, `${message} (${stats.failed} failed)`);
 } else {
 toast.success(null, message);
 }
 };

 const onError = () => {
 toast.error(null, 'Bulk retry failed');
 };

 if (activeTab === 'queue') {
 retryAllQueueJobs.mutate(limit, { onSuccess, onError });
 } else if (activeTab === 'outbox') {
 retryAllOutboxEvents.mutate(limit, { onSuccess, onError });
 } else {
 retryAllStripeWebhooks.mutate(limit, { onSuccess, onError });
 }
 };

 const totalFailed =
 (summary?.queue_failed ?? 0) +
 (summary?.outbox_failed ?? 0) +
 (summary?.stripe_failed ?? 0);

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Failed operations"
 subtitle="Retry failed queue jobs, outbox publishes, and Stripe webhook processing."
 actions={
 <>
 <Link
 to="/admin/performance"
 className="inline-flex items-center justify-center px-4 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-ink hover:bg-surface-muted"
 >
 Infrastructure overview
 </Link>
 <button
 type="button"
 onClick={() => refetch()}
 disabled={isLoading}
 className="inline-flex items-center gap-2 px-3 py-2.5 border border-border rounded-md text-sm font-medium bg-surface text-ink hover:bg-surface-muted disabled:opacity-50"
 >
 <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
 Refresh
 </button>
 </>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.failedOperations.title}
 steps={ADMIN_PAGE_GUIDES.failedOperations.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 {tabs.map((tab) => (
 <button
 key={tab.id}
 type="button"
 onClick={() => setActiveTab(tab.id)}
 className={`text-left rounded-lg border p-4 transition-colors shadow-sm ${ activeTab === tab.id ? 'border-ink/60 bg-surface-muted/80' : 'border-border bg-surface hover:border-ink' }`}
 >
 <p className="text-sm text-ink-secondary">{tab.label}</p>
 <p className={`text-2xl font-bold tabular-nums mt-1 ${tab.count > 0 ? 'text-red-600' : 'text-ink'}`}>
 {tab.count}
 </p>
 </button>
 ))}
 </div>

 {totalFailed === 0 && !isLoading && (
 <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2">
 <Server className="w-4 h-4" />
 No failed operations right now.
 </div>
 )}

 <div className="rounded-lg border border-border bg-surface shadow-sm overflow-hidden">
 <div className="border-b border-border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <div className="flex flex-wrap items-center gap-3">
 <div
 className="inline-flex flex-wrap rounded-full bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Failed operation type"
 >
 {tabs.map((tab) => {
 const isActive = activeTab === tab.id;
 return (
 <button
 key={tab.id}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setActiveTab(tab.id)}
 className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {tab.label}
 <span className={`text-xs tabular-nums ${tab.count > 0 ? 'text-red-500' : 'text-gray-400'}`}>
 {tab.count}
 </span>
 </button>
 );
 })}
 </div>
 <div className="hidden sm:flex items-center gap-2 text-gray-500">
 <Zap className="w-4 h-4" />
 <span className="text-sm font-medium text-ink">
 {activeTabConfig?.label}
 </span>
 </div>
 </div>
 {(activeTabConfig?.count ?? 0) > 0 && (
 <RetryButton
 label="Retry all"
 disabled={isRetrying}
 onClick={handleBulkRetry}
 />
 )}
 </div>

 {isLoading ? (
 <div className="py-16 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading failed operations…</p>
 </div>
 ) : (
 <>
 {activeTab === 'queue' && (
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-surface-muted/60">
 <tr>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Queue</th>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed at</th>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
 <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-border">
 {queueJobs.length === 0 ? (
 <EmptyRow message="No failed queue jobs." />
 ) : (
 queueJobs.map((job) => (
 <tr key={job.uuid}>
 <td className="px-4 py-3 text-sm font-medium text-ink">{job.job_name}</td>
 <td className="px-4 py-3 text-sm text-ink-secondary">{job.queue}</td>
 <td className="px-4 py-3 text-sm text-ink-secondary">
 {job.failed_at ? (
 <>
 {formatDate(job.failed_at, 'medium')}
 <span className="block text-xs text-gray-500">{formatTime(job.failed_at)}</span>
 </>
 ) : (
 '—'
 )}
 </td>
 <td className="px-4 py-3 text-xs text-red-700 max-w-xs truncate" title={job.exception}>
 {job.exception || '—'}
 </td>
 <td className="px-4 py-3 text-right">
 <RetryButton
 disabled={isRetrying}
 onClick={() => retryQueueJob.mutate(job.uuid)}
 />
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>
 )}

 {activeTab === 'outbox' && (
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-surface-muted/60">
 <tr>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topic</th>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
 <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-border">
 {outboxEvents.length === 0 ? (
 <EmptyRow message="No failed outbox events." />
 ) : (
 outboxEvents.map((event) => (
 <tr key={event.id}>
 <td className="px-4 py-3 text-sm font-medium text-ink">{event.event_type}</td>
 <td className="px-4 py-3 text-sm text-ink-secondary font-mono">{event.topic}</td>
 <td className="px-4 py-3 text-sm text-ink-secondary tabular-nums">{event.attempts}</td>
 <td className="px-4 py-3 text-xs text-red-700 max-w-xs truncate" title={event.last_error}>
 {event.last_error || '—'}
 </td>
 <td className="px-4 py-3 text-right">
 <RetryButton
 disabled={isRetrying}
 onClick={() => retryOutboxEvent.mutate(event.id)}
 />
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>
 )}

 {activeTab === 'stripe' && (
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-surface-muted/60">
 <tr>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event type</th>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stripe event ID</th>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
 <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
 <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-border">
 {stripeWebhooks.length === 0 ? (
 <EmptyRow message="No failed Stripe webhooks." />
 ) : (
 stripeWebhooks.map((webhook) => (
 <tr key={webhook.id}>
 <td className="px-4 py-3 text-sm font-medium text-ink">{webhook.event_type}</td>
 <td className="px-4 py-3 text-sm text-ink-secondary font-mono truncate max-w-[12rem]">{webhook.event_id}</td>
 <td className="px-4 py-3 text-sm text-ink-secondary">
 {webhook.updated_at ? (
 <>
 {formatDate(webhook.updated_at, 'medium')}
 <span className="block text-xs text-gray-500">{formatTime(webhook.updated_at)}</span>
 </>
 ) : (
 '—'
 )}
 </td>
 <td className="px-4 py-3 text-xs text-red-700 max-w-xs truncate" title={webhook.error_message}>
 {webhook.error_message || '—'}
 </td>
 <td className="px-4 py-3 text-right">
 <RetryButton
 disabled={isRetrying}
 onClick={() => retryStripeWebhook.mutate(webhook.id)}
 />
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>
 )}
 </>
 )}
 </div>

 {activeMeta?.last_page > 1 && (
 <Pagination
 currentPage={activeMeta.current_page}
 lastPage={activeMeta.last_page}
 perPage={activeMeta.per_page || perPage}
 total={activeMeta.total}
 onPageChange={handlePageChange}
 scrollToTop={false}
 />
 )}
 </div>
 );
};

export default AdminFailedOperations;
